/**
 * Camera math shared by the renderer and input controller. Camera position is
 * expressed in tile coordinates (top-left of the viewport); tile size is a
 * fixed pixel constant scaled by the camera's zoom factor.
 */

export const TILE_SIZE = 24;
export const MIN_ZOOM = 0.6;
export const MAX_ZOOM = 1.6;

export interface Camera {
  x: number;
  y: number;
  zoom: number;
  /** Extra scrollable space (CSS px) past the map's bottom edge, matching
   * whatever HUD panel currently covers the bottom of the viewport — lets
   * bottom-of-map content be panned up above the panel. Maintained by the
   * renderer each frame; every clampCamera caller respects it for free. */
  bottomInsetPx?: number;
}

/** Effective on-screen pixel size of one world tile at the camera's current zoom. */
export function effectiveTileSize(cam: Camera): number {
  return TILE_SIZE * cam.zoom;
}

export function viewportTiles(cssWidth: number, cssHeight: number, zoom = 1): { vw: number; vh: number } {
  const ts = TILE_SIZE * zoom;
  return { vw: cssWidth / ts, vh: cssHeight / ts };
}

export function clampCamera(
  cam: Camera,
  mapWidth: number,
  mapHeight: number,
  cssWidth: number,
  cssHeight: number,
): void {
  const { vw, vh } = viewportTiles(cssWidth, cssHeight, cam.zoom);
  const insetTiles = (cam.bottomInsetPx ?? 0) / (TILE_SIZE * cam.zoom);
  const maxX = Math.max(0, mapWidth - vw);
  const maxY = Math.max(0, mapHeight - vh + insetTiles);
  cam.x = Math.max(0, Math.min(maxX, cam.x));
  cam.y = Math.max(0, Math.min(maxY, cam.y));
}

export function worldToScreen(cam: Camera, wx: number, wy: number): { x: number; y: number } {
  const ts = TILE_SIZE * cam.zoom;
  return { x: (wx - cam.x) * ts, y: (wy - cam.y) * ts };
}

export function screenToWorld(cam: Camera, sx: number, sy: number): { x: number; y: number } {
  const ts = TILE_SIZE * cam.zoom;
  return { x: cam.x + sx / ts, y: cam.y + sy / ts };
}

/**
 * Set the camera's zoom (clamped to [MIN_ZOOM, MAX_ZOOM]) while keeping the
 * world point currently under screen coordinate (sx, sy) fixed under that
 * same screen point after the zoom change. Used by both scroll-wheel zoom
 * and pinch-to-zoom so the content under the cursor/fingers doesn't jump.
 */
export function setZoom(cam: Camera, zoom: number, sx: number, sy: number): void {
  const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom));
  if (clamped === cam.zoom) return;
  const before = screenToWorld(cam, sx, sy);
  cam.zoom = clamped;
  const after = screenToWorld(cam, sx, sy);
  cam.x += before.x - after.x;
  cam.y += before.y - after.y;
}
